import { guestOffersFetch } from './guestOffersApi';

export type CommercialSurface = 'city_feed_inline' | 'city_offers_featured' | 'evening_showcase';
export type CommercialSubjectType = 'offer' | 'event' | 'venue';

export type SponsoredPlacement = {
  id: string;
  creative_version: number;
  creative_hash: string;
  starts_at: string;
  ends_at: string;
  subject: {
    type: CommercialSubjectType;
    id: string;
    destination: string;
  };
  title: string;
  description: string;
  media: null | {
    url: string;
    thumbnail_url: string | null;
  };
  venue: {
    id: string;
    name: string | null;
    address: string | null;
  };
  advertisement: null | {
    label: 'Реклама' | string;
    advertiser_name: string;
    advertiser_inn: string;
    erid: string;
  };
  delivery_token: string;
};

export type SponsoredPlacementResponse = {
  data: {
    surface: CommercialSurface;
    city: {
      id: string;
      name: string;
      timezone: string;
    };
    items: SponsoredPlacement[];
    served_at: string;
  };
};

type AcceptedResponse = { data: { accepted: boolean } };

type StoredOfferReference = {
  token: string;
  storedAt: number;
};

const OFFER_REFERENCE_PREFIX = 'hopper.commercial.offer-reference.';
const OFFER_REFERENCE_TTL_MS = 10 * 60 * 1000;

export function newPageViewId(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  globalThis.crypto?.getRandomValues?.(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function fetchSponsoredPlacement(
  city: string,
  surface: CommercialSurface,
  pageViewId: string,
): Promise<SponsoredPlacementResponse> {
  const params = new URLSearchParams({ surface, page_view_id: pageViewId });
  return guestOffersFetch<SponsoredPlacementResponse>(
    `/api/v1/cities/${encodeURIComponent(city)}/commercial/placements?${params.toString()}`,
  );
}

export function recordSponsoredEvent(
  deliveryToken: string,
  kind: 'visible_impression' | 'open',
): Promise<AcceptedResponse> {
  return guestOffersFetch<AcceptedResponse>('/api/v1/commercial/events', {
    method: 'POST',
    body: JSON.stringify({
      delivery_token: deliveryToken,
      kind,
      client_event_at: new Date().toISOString(),
    }),
  });
}

export function rememberSponsoredOfferReference(offerId: string, deliveryToken: string): void {
  try {
    const value: StoredOfferReference = { token: deliveryToken, storedAt: Date.now() };
    sessionStorage.setItem(`${OFFER_REFERENCE_PREFIX}${offerId}`, JSON.stringify(value));
  } catch {
    // Attribution is additive. Storage failure must not affect navigation/core Offers.
  }
}

export function sponsoredOfferReference(offerId: string): string | null {
  try {
    const raw = sessionStorage.getItem(`${OFFER_REFERENCE_PREFIX}${offerId}`);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<StoredOfferReference>;
    if (typeof value.token !== 'string' || typeof value.storedAt !== 'number') {
      sessionStorage.removeItem(`${OFFER_REFERENCE_PREFIX}${offerId}`);
      return null;
    }
    if (Date.now() - value.storedAt >= OFFER_REFERENCE_TTL_MS) {
      sessionStorage.removeItem(`${OFFER_REFERENCE_PREFIX}${offerId}`);
      return null;
    }
    return value.token;
  } catch {
    return null;
  }
}

export function clearSponsoredOfferReference(offerId: string): void {
  try {
    sessionStorage.removeItem(`${OFFER_REFERENCE_PREFIX}${offerId}`);
  } catch {
    // no-op
  }
}
